import { InvestorStatus } from 'src/app/models/investor';
import { initialTradingState } from '../../store/state/order.state';
import { IRegistrationKiwiSaverEntityState, initialRegistrationKiwiSaverEntityState } from '../../store/state/registration-kiwisaver.state';
import { UserFacade } from 'src/app/store/user/facade/user.facade';
import { RegistrationKiwiSaverFacade } from '../../store/registration-kiwisaver/facade/kiwisaver-registration.facade';
import { isEmptyObject } from '../../utils/empty-object-check';
import { TradingFacades } from 'src/app/store/order/facades/trading.facades';
import { InvestmentPlan } from 'src/app/models/orders';
import { Observable, combineLatest, Subscription, zip } from 'rxjs';
import { AuthFacade } from 'src/app/store/auth/facade/auth.facade';
import { Router, NavigationEnd } from '@angular/router';
import { filter, map, distinctUntilChanged, pairwise } from 'rxjs/operators';
import { Injectable, OnDestroy } from "@angular/core";
import { RegularInvestmentPlanFacade } from 'src/app/store/regular-investment-plan/regular-investment-plan.facade';


export enum registrationStatus {
    NEW_USER, USER_SETUP, USER_REGISTERED, USER_ACTIVE
}

export type PrevNewEntityIP = [
    [IRegistrationKiwiSaverEntityState, IRegistrationKiwiSaverEntityState],
    [boolean, boolean],
];



@Injectable()
export class GoogleTagManagerService implements OnDestroy {

    private selectedUserEntityId: string;
    private investmentPlan$: Observable<Partial<InvestmentPlan>> = this.regularInvestmentPlanFacade.getRegularInvestmentPlan().pipe(
        filter((ip) => ip !== initialTradingState.investmentPlan),
        map((ip) => {
            return ip;
        })
    );
    private kiwiSaverExistingEntity$ = this.registrationKiwiSaverFacade.getKiwisaverEntity().pipe(
        map((entity) => {
            return entity;
        })
    );
    private kiwiSaverRegistrationStepsParams$: Observable<[IRegistrationKiwiSaverEntityState[], boolean[]]> = combineLatest([
        this.kiwiSaverExistingEntity$.pipe(
            pairwise(),
            map(([prevEntity, newEntity]) => {
                return [prevEntity, newEntity];
            }),
            filter(([prevEntity, newEntity]) => {
                /**
                 * When a user submits the registration form, an entity id is created. This entity id is necessary to decide if a user has signed up.
                 * We're only interested in receiving values when the state value changes from non existent to existent. From going from new user to signed up.
                 * the signup stage is the stage before complete registration. In order to be fully registered, the user has to create an investment plan (next observable).
                 */
                const prevEntityIDDoesntExist = !(prevEntity?.entityId); // this covers both the scenarios when a user logs in after previously been setup (but not registerd), or when signing up and continuing to submitting an investment plan and gets registered straight away. Either prevEntity exists but no entityID or no prevEntity exists, and thus also no entityID
                const newEntityIDExists = !!newEntity.entityId;
                return (prevEntityIDDoesntExist && newEntityIDExists);
            })
        ),
        this.investmentPlan$.pipe(
            pairwise(),
            filter(([prevIP, newIP]) => {
                /**
                 * When a user creates an investment plan, he will become a registered kiwisaver investor.
                 * We're only interested in receiving values when the investment plan is an empty object (which is created on the backend when a user has signed up),
                 * or when it changes to an investment plan wth positions. Thus going from signed up to fully registered. When combining these observables we can determine when a user signs up or registers (see kiwiSaverRegistrationStatusSubscription).
                 */
                return (isEmptyObject(prevIP) && !isEmptyObject(newIP)) || isEmptyObject(prevIP) && isEmptyObject(newIP);
            }),
            map((v) => {
                const prevIp = v[0];
                const newIp = v[1];
                return [isEmptyObject(prevIp), isEmptyObject(newIp)];
            }),
            distinctUntilChanged()
        )
    ]);
    private kiwiSaverLoginStatusParams$: Observable<[IRegistrationKiwiSaverEntityState, boolean]> = zip(
        this.kiwiSaverExistingEntity$.pipe(
            distinctUntilChanged()
        ),
        this.investmentPlan$.pipe(
            map((ip) => {
                return isEmptyObject(ip);
            }),
            distinctUntilChanged())
        /**
         * zip means that both observables need to have emitted a value, before both values will be send through
         * distinctUntilChanged only sends values when the current one differs from the previous one.
         * So this works as follows: We only send the values from the kiwiSaverEntity and investmentPlan state when they are explicitly set,
         * since we filter the initial state out of the KiwiSaver entity and investment plan when initialising the observables in the beginning of the document.
         * then we make sure only new or changed values are send through. Which won't happen, since this observable will only be subscribed to until data is received after logging in (see subscribeUserLoginStatusSubscription)
         */
    );
    private isUserLoggedIn$: Observable<boolean> = this.authFacade.userLoggedIn$();
    private isUserLoggedInSubscription: Subscription;
    private loginStatusSubscription: Subscription;
    private kiwiSaverRegistrationStatusSubscription: Subscription;

    constructor(
        private router: Router,
        private authFacade: AuthFacade,
        private registrationKiwiSaverFacade: RegistrationKiwiSaverFacade,
        private userFacade: UserFacade,
        private regularInvestmentPlanFacade: RegularInvestmentPlanFacade) {
        this.subscribeUserId();
        this.setupRouteLogging();
        this.subscribeUserLoginStatus(); // the login status drives the other subscriptions
    }

    private browserGlobals = {
        windowRef(): any {
            return window;
        },
        documentRef(): any {
            return document;
        },
    };

    private logTag(obj: object) {
        const dataLayer = this.getDataLayer();
        const tag = {
            ...obj,
            'userId': this.selectedUserEntityId
        };
        dataLayer.push(tag);
    }

    private subscribeUserLoginStatus() {
        this.isUserLoggedInSubscription = this.isUserLoggedIn$.subscribe((isLoggedIn: boolean) => {
            if (isLoggedIn) {
                this.subscribeToKiwiSaverRegistrationStatus();
                this.subscribeUserLoginStatusSubscription();
            } else {
                this.removeSubscriptions();
            }
        });
    }

    private removeSubscriptions() {
        if (this.kiwiSaverRegistrationStatusSubscription) {
            this.kiwiSaverRegistrationStatusSubscription.unsubscribe();
        }
        if (this.loginStatusSubscription) {
            this.loginStatusSubscription.unsubscribe();
        }
    }

    private setupRouteLogging() {
        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                const gtmTag = {
                    event: 'content-view',
                    'content-name': event.urlAfterRedirects
                };
                this.logTag(gtmTag);
            }
        });
    }

    private subscribeToKiwiSaverRegistrationStatus() {
        if (this.kiwiSaverRegistrationStatusSubscription) {
            this.kiwiSaverRegistrationStatusSubscription.unsubscribe();
        }

        this.kiwiSaverRegistrationStatusSubscription = this.kiwiSaverRegistrationStepsParams$.pipe(
            distinctUntilChanged()
        ).subscribe((values: PrevNewEntityIP) => {
            const [regStatus, shouldUnsubscribe]: [registrationStatus, boolean] = this.getRegStatus(values);

            let event: string;
            if (regStatus === registrationStatus.USER_SETUP) {
                event = 'ks-sign-up-success';
            } else if (regStatus === registrationStatus.USER_REGISTERED) {
                event = 'ks-final-registration-success';
            }

            if (!!event) {
                this.logTag({ 'event': event });
            }

            if (shouldUnsubscribe) {
                this.kiwiSaverRegistrationStatusSubscription.unsubscribe();
            }


        });
    }

    private getRegStatus(values: PrevNewEntityIP): [registrationStatus, boolean] {
        const [prevEntity, newEntity] = values[0];
        const [isPrevEmptyInvestmentPlan, isNewEmptyInvestmentPlan] = values[1];

        const shouldUnsubscribe = newEntity.entityId && !isNewEmptyInvestmentPlan;

        if ((prevEntity && !prevEntity.entityId && newEntity.entityId) && isNewEmptyInvestmentPlan) {
            return [registrationStatus.USER_SETUP, shouldUnsubscribe];
        }

        if (newEntity?.entityId && (isPrevEmptyInvestmentPlan && !isNewEmptyInvestmentPlan)) {
            return [registrationStatus.USER_REGISTERED, shouldUnsubscribe];
        }

        return [null, shouldUnsubscribe];

    }

    private subscribeUserLoginStatusSubscription() {
        if (this.loginStatusSubscription) {
            this.loginStatusSubscription.unsubscribe();
        }

        this.loginStatusSubscription = combineLatest([
            this.kiwiSaverLoginStatusParams$,
            this.isUserLoggedIn$
        ]).subscribe((values: [[IRegistrationKiwiSaverEntityState, boolean], boolean]) => {
            const [kiwisaverEntity, isEmptyInvestmentPlan] = values[0];
            const isUserLoggedIn = values[1];
            if (isUserLoggedIn) {

                let gtmTagEvent: {
                    'event': string,
                    'KSPortfolioHasFunds'?: boolean
                } = {
                    'event': 'ks-login'
                };

                if (kiwisaverEntity?.entityId && !isEmptyInvestmentPlan) {
                    gtmTagEvent = {
                        ...gtmTagEvent,
                        'KSPortfolioHasFunds': true
                    };
                }

                this.logTag(gtmTagEvent);

                // only send tag once after logging in. Not again when ip has changed
                if (this.loginStatusSubscription) {
                    this.loginStatusSubscription.unsubscribe();
                }
            }
        });
    }

    private subscribeUserId() {
        this.userFacade.userEntityId$.subscribe((userEntityId: string) => {
            this.selectedUserEntityId = userEntityId;
        });
    }


    private getDataLayer() {
        const window = this.browserGlobals.windowRef();
        window['dataLayer'] = window['dataLayer'] || [];
        return window['dataLayer'];
    }

    ngOnDestroy() { }
}
